import { EnergyType } from '@energy-retailer/constants';
import { useId } from 'react';

import { formatAnnualConsumption } from '@/app/lib/utils';
import { Text } from '@/components/text';
import { Tooltip } from '@/components/tooltip';

import type { CmsPlanSchema } from '@/app/lib/hooks/use-plans-cms';

type Props = {
  plan: NonNullable<CmsPlanSchema>;
};

const copyBuilder = (
  plan: NonNullable<CmsPlanSchema>,
  accesibilityId: string
) => {
  const tariff =
    plan.energyType === EnergyType.Electricity
      ? plan.tariffType.replace(/ \d$/, '') // remove trailing 1 or 2 from controlled load tariff
      : '';

  const defaultCopy = (
    <>
      *This{' '}
      <a
        className="text-info underline decoration-info"
        href="#!"
        data-tooltip-id={`my-price-tooltip-${accesibilityId}`}
        aria-describedby={`my-price-tooltip-${accesibilityId}`}
      >
        estimated price
      </a>{' '}
      {plan.product.signupCredit ? (
        <>
          {plan.energyType === EnergyType.Electricity
            ? 'and % calculations are'
            : 'is'}{' '}
          inclusive of the ${plan.product.signupCredit} (incl GST) sign up
          credit and{' '}
        </>
      ) : (
        'is '
      )}
      based on an average {plan.customerType.toLowerCase()} customer with a{' '}
      {tariff} tariff using{' '}
      {formatAnnualConsumption(plan.annualConsumption, plan.energyType)} per
      year in the {plan.distributor.name} network. Your actual bills will vary
      based on your usage rates and tariff type. We may vary these prices but we
      will always give you advance notice.
    </>
  );

  if (!plan.isVictorianEVPlanWithMappedDistributor || !plan.evLoadProfile) {
    return defaultCopy;
  }

  return (
    <>
      <a
        className="text-info underline decoration-info"
        href="#!"
        data-tooltip-id={`my-price-tooltip-${accesibilityId}`}
        aria-describedby={`my-price-tooltip-${accesibilityId}`}
      >
        This estimated price
      </a>{' '}
      and % calculations in the first year include the $
      {plan.product.signupCredit} (incl GST) sign up credit and are based on an
      average residential customer with an electric vehicle, on a {tariff}{' '}
      tariff in the {plan.distributor.name} network, using{' '}
      {formatAnnualConsumption(plan.annualConsumption, plan.energyType)} per
      year and charging their electric vehicle during the super off-peak period.
      This assumes use of {plan.evLoadProfile.superOffPeak} electricity in the
      super off-peak period, {plan.evLoadProfile.offPeak} in the off peak
      period, and {plan.evLoadProfile.peak} in the peak period, with assumptions
      based on our average historical residential customer electric vehicle load
      profile as at 25 October 2024. Your actual bills will vary based on your
      usage, rates and tariff type. We may vary these prices but we will always
      give you advance notice.
    </>
  );
};

export const PlanTariffTooltip = ({ plan }: Props) => {
  const accesibilityId = useId();

  return (
    <div>
      <Text
        variant="body2"
        data-testid={`my-price-tooltip-${plan.planID}`}
      >
        {copyBuilder(plan, accesibilityId)}
      </Text>

      <Tooltip id={`my-price-tooltip-${accesibilityId}`}>
        We have calculated the monthly estimates based on the annual figure and
        divided by 12. Your actual bills will vary depending on your usage,
        rates, tariff and any price changes in the future. This estimate
        doesn&apos;t include concessions or other rebates, distributor service
        order costs, fees or, for electricity, solar feed-in tariff that may
        apply to you.
      </Tooltip>
    </div>
  );
};
